import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { Layout } from 'antd';
import { PropsWithChildren, useLayoutEffect, useMemo } from 'react';

import { getAccountOptions } from '@lib/api/options/auth';
import InternalRouteProvider from '@lib/providers/internal-route-provider';
import { accountNeedsCPF } from '@lib/types';
import { applyPageMetadata } from '@lib/utils/page-metadata';
import { findMatchingRoute } from '@lib/utils/route';
import { getAllModulesRoutes } from '@modules/index';
import { ROUTE_PATHS } from '@modules/route-paths';
import RequiredCPFModal from '@modules/user/components/required-cpf-modal';

import AdminLayout from '../admin-layout';
import AppFooter from '../app-footer';
import AppHeader from '../app-header';
import { PageAccountContext } from '../page/account-context';
import RouterSwitch from '../router';

const styles = {
  container: css`
    min-height: 100dvh;
    width: 100%;
    background: var(--ant-color-bg-layout);
  `,
  layout: css`
    min-width: 0;
    min-height: 100dvh;
    background: transparent;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    flex: 1;
    overflow: visible;
    background: transparent;
  `,
  page: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 100vh;

    > * {
      flex: 1;
    }
  `,
};

const Scaffold = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const routeTitle = useMemo(
    () => findMatchingRoute(getAllModulesRoutes(), location.pathname)?.title,
    [location.pathname],
  );

  useLayoutEffect(() => {
    applyPageMetadata(routeTitle, location.pathname);
  }, [routeTitle, location.pathname]);

  const accountQueryEnabled =
    location.pathname !== ROUTE_PATHS.SERVICE_UNAVAILABLE;
  const { data: account, isPending: loadingAccount } = useQuery({
    ...getAccountOptions(),
    enabled: accountQueryEnabled,
  });

  const showAppHeader =
    location.pathname !== ROUTE_PATHS.LOGIN &&
    location.pathname !== ROUTE_PATHS.REGISTER &&
    location.pathname !== ROUTE_PATHS.FORGOT_PASSWORD &&
    location.pathname !== ROUTE_PATHS.SERVICE_UNAVAILABLE;
  const pageAccountContext = useMemo(() => ({ account }), [account]);
  const isAdminArea = location.pathname.startsWith('/administracao');
  const routedContent = (
    <PageAccountContext.Provider value={pageAccountContext}>
      {children ?? <RouterSwitch />}
    </PageAccountContext.Provider>
  );

  return (
    <InternalRouteProvider>
      {accountNeedsCPF(account) && <RequiredCPFModal />}
      <Layout className={styles.container}>
        <Layout className={styles.layout}>
          {showAppHeader && (
            <AppHeader account={account} accountLoading={loadingAccount} />
          )}
          <Layout className={styles.content}>
            <div className={styles.page}>
              {isAdminArea ? (
                <AdminLayout>{routedContent}</AdminLayout>
              ) : (
                routedContent
              )}
            </div>
            {!loadingAccount ? <AppFooter /> : null}
          </Layout>
        </Layout>
      </Layout>
    </InternalRouteProvider>
  );
};

export default Scaffold;
